import React, { useEffect, useState } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import axiosConfig from "../../axiosConfig";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import Cookies from "js-cookie";
import { validateEmailField } from "../../utils/validation";

interface FormData {
  username: string;
  email: string;
  password: string;
  fullname: string;
  fullname_kana: string;
  phone_number: string;
  confirmPassword: string;
  type: string;
  [key: string]: string | "";
}

interface objectFormData {
  [key: string]: {
    max: number | null;
    min: number | null;
    name: string | null;
    required: boolean | false;
  };
}

const CustomerRegister: React.FC = () => {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const { email, phoneNumber, pic, picRuby } = Object.fromEntries(
    urlParams.entries()
  );

  const initialForm = {
    username: "",
    email: email ?? "",
    password: "",
    confirmPassword: "",
    fullname: pic ?? "",
    fullname_kana: picRuby ?? "",
    phone_number: phoneNumber ?? "",
    type: "customer",
  };
  const initialValidate = {
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    fullname: "",
    fullname_kana: "",
    phone_number: "",
  };
  const [errors, setErrors] = useState<Partial<FormData>>(initialValidate);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorServer, setErrorServer] = useState<string>("");
  const [formData, setFormData] = useState<FormData>(initialForm);
  const [loading, setLoading] = useState(false);

  const validate = (field: string, value: string) => {
    let error = "";
    const regexUsername: RegExp = /^[a-zA-Z0-9]+$/;
    const regexPassword: RegExp =
      /^[a-zA-Z0-9!"#$%&'()\-^@[\];:,./=~|`{+*}<>?_]+$/;
    const regexEmail: RegExp = /^[\w-.]+@([\w-]+\.)+[\w-]{2,63}$/;
    const regexFullname: RegExp =
      /^(?:[\u3041-\u3096\u30A1-\u30FA\u30FC\u4E00-\u9FFF\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\s]|[A-Za-z0-9])+$/;
    const regexFullnameKana = /^[\u30A1-\u30FA\u30FC\s]+$/;
    const regexPhone: RegExp = /^[0-9]+(-[0-9]+)*$/;

    const validateKeys: objectFormData = {
      username: {
        max: 20,
        min: 3,
        name: "ログインID",
        required: true,
      },
      password: {
        max: 50,
        min: 8,
        name: "パスワード",
        required: true,
      },
      fullname: {
        max: 50,
        min: 3,
        name: "氏名",
        required: true,
      },
      fullname_kana: {
        max: 50,
        min: 3,
        name: "氏名（カナ）",
        required: true,
      },
      phone_number: {
        max: null,
        min: null,
        name: "電話番号(ハイフンあり)",
        required: false,
      },
      confirmPassword: {
        max: 50,
        min: 8,
        name: "確認のため再度パスワードを入力",
        required: true,
      },
      email: {
        max: null,
        min: null,
        name: "メールアドレス",
        required: true,
      },
    };

    if (validateKeys[field].required && value.length === 0) {
      error = "必須項目です。";
    } else if (value !== "") {
      if (
        (field === "confirmPassword" && value !== formData.password) ||
        (field === "password" &&
          value !== formData.confirmPassword &&
          formData.confirmPassword !== "")
      ) {
        return setErrors({
          ...errors,
          confirmPassword: "入力されたパスワードが一致しません。",
        });
      }

      if (formData.confirmPassword !== "")
        if (
          validateKeys[field].max !== null &&
          validateKeys[field].min !== null
        ) {
          if (
            value.length > (validateKeys[field].max ?? 0) ||
            value.length < (validateKeys[field].min ?? 0)
          )
            error = `${validateKeys[field].min}～${validateKeys[field].max}文字以内で入力してください。`;
        }

      // Updated email validation logic using the utility function
      if (field === "email") {
        const emailValidation = validateEmailField(value);
        if (!emailValidation.isValid) {
          error = emailValidation.message;
        }
      } else if (
        (field === "username" && !regexUsername.test(value)) ||
        (field === "password" && !regexPassword.test(value)) ||
        (field === "confirmPassword" && !regexPassword.test(value)) ||
        (field === "fullname" && !regexFullname.test(value)) ||
        (field === "fullname_kana" && !regexFullnameKana.test(value)) ||
        (field === "phone_number" && !regexPhone.test(value))
      ) {
        error = `${validateKeys[field].name}の値が不正です。`;
      }

      if (field === "phone_number" && !value.includes("-")) {
        error = `ハイフンが含まれていません。`;
      }
    }
    setErrors({ ...errors, [field]: error });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    validate(name, value);
  };

  const validateRequiredAll = () => {
    let isValid = true;
    Object.keys(formData).forEach((key: string) => {
      if (formData[key].length === 0 && key !== "phone_number") {
        setErrors((errors) => {
          return { ...errors, [key]: "必須項目です。" };
        });
        isValid = false;
      }
    });
    return isValid;
  };

  //Handle Submit - FIX: Use async/await with proper error handling
  const handleSubmit = async () => {
    setErrorServer("");
    setLoading(true);
    let valid = true;
    if (!validateRequiredAll()) valid = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== "") {
        valid = false;
      }
    });

    if (valid) {
      try {
        const res = await axiosConfig({
          method: "post",
          url: "/auth/register",
          data: { ...formData },
        });

        if (res.status === 201 && res.data != null) {
          setFormData({ ...initialForm });
          setErrors({});
          setErrorServer("");
          setIsSuccess(true);
        }
      } catch (err: any) {
        setIsSuccess(false);
        if (err.response && err.response.status) {
          if (err.response.status === 409) {
            // FIX: Safe access with optional chaining
            const errorMessage =
              err.response.data?.body?.detail ||
              "このアカウントは既に存在します。";
            setErrorServer(errorMessage);
          } else {
            setErrorServer(
              `エラーが発生しました。${err.response.status}システムの管理者にお問い合わせください。`
            );
          }
        } else {
          setErrorServer(
            "ネットワークエラーが発生しました。再度お試しください。"
          );
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (Cookies.get("access_token")) {
      navigate("/");
    }
  }, []);
  return (
    <div className="bg-gray-100 flex flex-col" style={{ minHeight: "100vh" }}>
      <Header></Header>
      <div className="my-auto flex">
        <Card
          className="mx-auto"
          sx={{ width: "350px", marginTop: "50px", marginBottom: "110px" }}
        >
          <CardContent>
            {!isSuccess && (
              <>
                <TextField
                  required
                  autoComplete="one-time-code"
                  margin="dense"
                  name="username"
                  value={formData.username}
                  label="ログインID"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.username}
                  error={Boolean(errors.username)}
                />
                <TextField
                  required
                  autoComplete="one-time-code"
                  margin="dense"
                  name="password"
                  value={formData.password}
                  label="パスワード "
                  type="password"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.password}
                  error={Boolean(errors.password)}
                />
                <TextField
                  required
                  autoComplete="one-time-code"
                  margin="dense"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  label="確認のため再度パスワードを入力"
                  type="password"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.confirmPassword}
                  error={Boolean(errors.confirmPassword)}
                />
                <TextField
                  required
                  margin="dense"
                  name="fullname"
                  value={formData.fullname}
                  label="氏名"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.fullname}
                  error={Boolean(errors.fullname)}
                />
                <TextField
                  required
                  margin="dense"
                  name="fullname_kana"
                  value={formData.fullname_kana}
                  label="氏名（カナ）"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.fullname_kana}
                  error={Boolean(errors.fullname_kana)}
                />
                <TextField
                  required
                  margin="dense"
                  name="email"
                  value={formData.email}
                  label="メールアドレス"
                  type="email"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.email}
                  error={Boolean(errors.email)}
                />
                <TextField
                  required
                  margin="dense"
                  name="phone_number"
                  value={formData.phone_number}
                  label="携帯電話番号(ハイフンあり)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.phone_number}
                  error={Boolean(errors.phone_number)}
                />
              </>
            )}
            <>
              {/* FIX: Replace <p> tag with Material-UI Alert component */}
              {errorServer && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error" onClose={() => setErrorServer("")}>
                    {errorServer}
                  </Alert>
                </Box>
              )}

              {isSuccess && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success" onClose={() => setIsSuccess(false)}>
                    登録に成功しました。メールを確認してアカウントを認証してください。
                  </Alert>
                </Box>
              )}
            </>

            <Box sx={{ "& button": { mt: 2 } }}>
              <LoadingButton
                className="w-full"
                onClick={handleSubmit}
                variant="contained"
                loading={loading}
                disabled={isSuccess}
              >
                登録
              </LoadingButton>
              <Button
                className="w-full "
                variant="contained"
                color="success"
                onClick={() => navigate("/login")}
                disabled={loading}
              >
                ログイン画面に戻る
              </Button>
            </Box>
          </CardContent>
        </Card>
      </div>
      <Footer></Footer>
    </div>
  );
};

export default CustomerRegister;
